import * as React from "react";

import { cn } from "../utils";
import { IconTooltip } from "./icon-tooltip";

/**
 * A single meta fact: an icon that stands in for a label, plus a value. The
 * label name shows on hover as a styled tooltip (never the native `title=`),
 * centered on the icon alone — not the whole icon+value run.
 *
 * `icon` is PRE-RENDERED JSX (a `ReactNode`), not a component reference, so a
 * server component can build the fact without a lucide component ref crossing
 * the server→client boundary — the same rule `NavTab.icon` follows. Promoted
 * from the `/styleguide` `DetailHeader` prototype (docs/design/
 * PAGE-HEADER-UNIFICATION-PRD.md).
 */
export interface MetaFact {
  /** Pre-rendered icon JSX (e.g. `<ShieldCheck className="size-3.5" />`). */
  icon: React.ReactNode;
  /** The fact's name, surfaced on hover over the icon. */
  label: string;
  value: React.ReactNode;
}

function MetaFactItem({ icon, label, value }: MetaFact) {
  return (
    <span className="inline-flex items-center gap-1.5 type-subtext text-foreground">
      <IconTooltip label={label}>
        <span className="inline-flex cursor-default text-muted-foreground">
          {icon}
        </span>
      </IconTooltip>
      {value}
    </span>
  );
}

/**
 * The masthead meta-facts strip — a wrapped row of icon+value facts under a
 * page's description (Domain · Gate · Runs as · Live…). Used by `PageHeader` via
 * its `meta` prop; exported standalone for the rare surface that renders facts
 * outside a header.
 */
export function MetaFacts({
  facts,
  className,
}: {
  facts: MetaFact[];
  className?: string;
}) {
  if (facts.length === 0) return null;
  return (
    <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1", className)}>
      {facts.map((f, i) => (
        <MetaFactItem key={`${f.label}-${i}`} {...f} />
      ))}
    </div>
  );
}
